import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { css } from '@emotion/core'
import zionDebug from '@fs/zion-debug'
import { useFeatureFlags } from '@fs/zion-flags'
import ZionForm, { FormToggle, useZionForm } from '@fs/zion-form'
import { ContentAdd, NoticeLoading } from '@fs/zion-icon'
import {
  batchCreateRelationship,
  generateSls,
  getPersonRelationships,
  saveSLSActions,
  useSlsDispatchContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import {
  BillboardText,
  colors,
  DataBlock,
  Divider,
  H2,
  Image,
  InfoSheetPanel,
  Paragraph,
  Row,
  Separator,
  Subheading,
  useOverlay,
} from '@fs/zion-ui'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ErrorOverlay from '../../../../../../shared/components/errorOverlay/ErrorOverlay'
import PanelHeader from '../../../../../components/generic/panels/shared/PanelHeader'
import { useR2StateContext } from '../../../../../providers/r2Provider/R2Context'
import searchRecordImg from '../../../../../images/search-record.png'
import { headers, R2AppName } from '../../../../../../shared/services/config'
import { errorCollector } from '../../../../../../shared/utils/utils'
import useRecords from '../../../hooks/useRecords'
import { useOverlayContext } from '../../../../../../shared/providers/overlayContextProvider/OverlayContext'
import CrossRecordWarningOverlay from '../../editPanels/CrossRecordWarningOverlay'
import { dashedBorderCss } from '../../ui/Draggable'
import Droppable from '../../ui/Droppable'
import DraggablePerson from './DraggablePerson'
import { generateSaveDispatches } from '../../../../../components/generic/shared/utils/movePersonsUtils'
import { onDragCancelHandler, onDragEndHandler, onDragStartHandler } from './dragHandlers'
import DragOverlay from './DragOverlay'
import DroppableRecord from './DroppableRecord'
import NewRecordDialogOverlay from './NewRecordDialogOverlay'
import { useSharedBrowserStateContext } from '../../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

const activeBorder = css`
  outline: solid 0.25rem ${colors.blue10};
  background-image: none;
  outline-offset: -0.25rem;
`

const addRecordBoxNormalCss = css`
  background-image: ${dashedBorderCss};
  padding: 1rem;
  background-color: ${colors.gray03};
  outline: solid 0.25rem ${colors.gray00};
`

export const formInitialValues = {
  dragInfo: null,
  draggedPerson: {},
  newRecordType: undefined, // !IMPORTANT This NEEDS to be undefined to match the FormAutosuggest type definition
  selectedPersonsIds: [],
  destinationRecord: null,
  warningPerson: null,
  isMultiSelect: false,
  crossRecordRelationships: [],
}

const sensorsActivationConstraint = {
  delay: 150,
  distance: 5,
}

const debug = zionDebug('rip:R2:DragNames')

const DragNames = () => {
  const r2State = useR2StateContext()
  const zionSlsState = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { currentImageArk, loading, images } = zionSlsState
  const dispatch = useSlsDispatchContext()
  const { dragNamesOverlay, closeOverlay } = useOverlayContext()
  const [t] = useTranslation()
  const [error, setError] = useState(false)
  const [multipleRelationShipsWarning, setMultipleRelationShipsWarning] = useState(false)
  const [warningPerson, setWarningPerson] = useState()
  const [crossRecordRelationships, setCrossRecordRelationships] = useState([])
  const [destRecord, setDestRecord] = useState()
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag } = useFeatureFlags(['rip_sharedImageViewer_saveChanges'])

  const heading = t('drag=names.heading', 'Drag Names')
  const instructions = t(
    'drag-names.instructions',
    'Drag names to the correct record group or create a new record. All previous relationships will be deleted once a change is saved.'
  )

  const formMethods = useZionForm({
    defaultValues: formInitialValues,
  })

  const { allRecords, floatingPersons, noPeople } = useRecords({
    images: zionSlsState?.images,
    currentImageArk: zionSlsState?.currentImageArk,
    thumbnails,
  })
  const newRecordOverlay = useOverlay()
  const crossRecordWarningOverlay = useOverlay()

  const saveSls = useCallback(
    async (dispatches) => {
      const newRecordDispatch = dispatches.find((r) => r.type === 'CREATE_ELEMENT' && r.value.elementType === 'RECORD')
      const newRecordId = newRecordDispatch?.value?.id || ''

      const sls = generateSls(dispatches)

      if (sls?.length) {
        debug('saveSls: ', { sls, dispatches })
        try {
          if (saveChangesFlag.isOn) {
            saveSLSActions({
              actions: sls,
              userId: r2State.userId,
              appName: R2AppName,
              fsUserAgentChain: headers['FS-User-Agent-Chain'],
            })
          } else {
            debug('Saving changes to SLS is disabled')
          }
          // We want to put the new record at the top so the user can see it was added.  Before it was going to the bottom.
          // They had to scroll to see it, which is annoying
          dispatch({
            type: 'BATCH_DISPATCHES',
            value: newRecordId ? dispatches.concat({ type: 'NEW_RECORD_AT_TOP', value: newRecordId }) : dispatches,
          })
        } catch (err) {
          setError(err)
          errorCollector(err, {
            codeId: 66,
            sls,
            status: err?.response?.status,
            responseBody: err?.response?.data,
          })
        } finally {
          formMethods.reset()
        }
      }
    },
    [dispatch, r2State.userId, saveChangesFlag.isOn, formMethods]
  )

  const savePersonInOtherRecord = useCallback(
    async (recordIdParam, personsData, currentPartitionKey) => {
      let dispatches = []
      let relationshipDispatches = []
      const [isMultiSelect, newRecordType] = formMethods.getValues(['isMultiSelect', 'newRecordType'])

      if (isMultiSelect) {
        const asyncCollection = (
          await Promise.all(
            personsData.map(async (person) => {
              const allRelationPerson = getPersonRelationships(person, images)

              // Filter relationships that aren’t part of personsData
              const missingRelationships = allRelationPerson.filter(
                (relation) => !personsData.some((p) => p.id === relation.otherPersonId)
              )

              // If there are missing relationships
              if (missingRelationships.length > 0) {
                if (!warningPerson) {
                  setWarningPerson(personsData)
                  setMultipleRelationShipsWarning(true)
                  setDestRecord(recordIdParam)

                  return false
                }
                setWarningPerson(null)
              }

              // Filter relationships for personsData only
              const validRelations = allRelationPerson.filter((relation) =>
                personsData.some((personData) => personData.id === relation.otherPersonId)
              )

              validRelations.forEach((otherPerson) => {
                // verify if is second or first
                const isSecond = otherPerson.relKey.includes('_SECOND')
                const firstPerson = isSecond ? person : otherPerson
                const secondPerson = isSecond ? otherPerson : person
                const primaryRelation = batchCreateRelationship({
                  partitionKey: firstPerson.partitionKey,
                  person1ID: firstPerson.id,
                  person2ID: secondPerson.otherPersonId,
                  person1PartitionKey: firstPerson.partitionKey,
                  person2PartitionKey: secondPerson.partitionKey,
                  relType: secondPerson.fieldType,
                })

                // if the relation is not primary we are not going to get anything.
                if (!primaryRelation) return

                relationshipDispatches = [...relationshipDispatches, ...primaryRelation]
              })

              return true
            })
          )
        ).flat()

        // we need to valid if we display the warning and stop the process
        if (asyncCollection.includes(false)) {
          return
        }

        dispatches = await generateSaveDispatches({
          recordIdParam,
          personsData,
          currentPartitionKey,
          images,
          newRecordType,
        })
      } else {
        // Check if the personData is an array or an object
        personsData = Array.isArray(personsData) ? personsData[0] : personsData

        const existingRelationships = getPersonRelationships(personsData, images)

        if (existingRelationships.length > 0) {
          if (!warningPerson) {
            setWarningPerson(personsData)
            setCrossRecordRelationships(existingRelationships)
            setDestRecord(recordIdParam)
            crossRecordWarningOverlay.handleClick()
            return
          }
          setWarningPerson(null)
        }

        const singleDispatches = await generateSaveDispatches({
          recordIdParam,
          personsData,
          currentPartitionKey,
          images,
          newRecordType,
        })

        dispatches.push(...singleDispatches)
      }

      const addDispatches = []
      const removeDispatches = []
      dispatches.forEach((disp) => {
        if (disp.type === 'REMOVE_ELEMENT' || disp.type === 'REM_SUB') {
          removeDispatches.push(disp)
        } else {
          addDispatches.push(disp)
        }
      })

      const allDispatches = [...removeDispatches, ...addDispatches, ...relationshipDispatches]
      await saveSls(allDispatches)
    },
    [crossRecordWarningOverlay, warningPerson, images, saveSls, formMethods]
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: sensorsActivationConstraint,
    }),
    useSensor(TouchSensor, {
      activationConstraint: sensorsActivationConstraint,
    })
  )

  const userClosePanel = useCallback(() => {
    closeOverlay('dragNames')
    formMethods.reset()
  }, [closeOverlay, formMethods])

  return (
    <ZionForm methods={formMethods} id="drag-names-panel">
      <DndContext
        sensors={sensors}
        onDragStart={({ active }) => onDragStartHandler({ active, formMethods, images, allRecords })}
        onDragEnd={({ active, over }) =>
          onDragEndHandler({
            active,
            over,
            formMethods,
            images,
            newRecordOverlay,
            currentImageArk,
            savePersonInOtherRecord,
          })
        }
        onDragCancel={() => onDragCancelHandler({ formMethods })}
      >
        <InfoSheetPanel
          title={t('drag-names.heading', 'Drag Names')}
          headingColor="purple"
          {...{ ...dragNamesOverlay, close: userClosePanel }}
          headerContent={
            <PanelHeader heading={heading} caption={instructions} headingSize="H5">
              <Droppable
                key="new_record"
                id="new_record"
                setOverStyle={(isOver) => (isOver ? [addRecordBoxNormalCss, activeBorder] : addRecordBoxNormalCss)}
              >
                <div data-testid="droppableChild">
                  <BillboardText primaryColor={colors.blue60} secondaryColor={colors.gray100}>
                    <Row gutters="nano">
                      <ContentAdd color={colors.blue60} size="xs" />
                      <H2 size="H6" supplementary>
                        {t('drag-names.button.newRecord', 'New Record Group')}
                        <Subheading>
                          {t('drag-names.button.dragToCreateRecord', 'Drag name to create a new record group.')}
                        </Subheading>
                      </H2>
                    </Row>
                  </BillboardText>
                </div>
              </Droppable>
            </PanelHeader>
          }
        >
          <>
            <FormToggle
              data-testid="toggle-multi-select"
              label={t('drag-names.toggle.multiple', 'Move multiple names')}
              name="isMultiSelect"
              onChange={() => {
                formMethods.setValue('selectedPersonsIds', [])
              }}
            />
            {/* <Divider marginY="xs" fullWidth /> */}
          </>

          {allRecords?.map((record) => (
            <DroppableRecord record={record} key={record?.id} />
          ))}

          {floatingPersons && floatingPersons?.length > 0 && (
            <>
              <Divider fullWidth marginY="xs" />
              <DataBlock
                heading={t('select-person.title.heading', 'Names not in records')}
                data={t('select-person.title.subheading', 'Add relationships to connect a name to the correct record.')}
              />
              <Separator size="xs" />
              {floatingPersons
                .sort((a, b) => (a.primary > b.primary ? -1 : 1))
                .map((person) => (
                  <DraggablePerson key={person?.id} person={person} />
                ))}
            </>
          )}
          {!loading && noPeople && (
            <>
              <Image src={searchRecordImg} alt="" />
              <Paragraph size="sm" centered>
                {t('select-person.subheading.not-indexed', 'This image has not been indexed.')}
              </Paragraph>
            </>
          )}
          {noPeople && loading && (
            <Row alignX="center" data-testid="loading-indicator">
              <NoticeLoading size="md" />
            </Row>
          )}

          {/* Temporary person draggable component that is detached from the list. It is used to drag the person. */}
          <DragOverlay />

          {newRecordOverlay.isOpen && (
            <NewRecordDialogOverlay
              newRecordOverlay={newRecordOverlay}
              savePersonInOtherRecord={savePersonInOtherRecord}
            />
          )}

          {warningPerson && (
            <CrossRecordWarningOverlay
              overlay={crossRecordWarningOverlay}
              person={warningPerson}
              existingRelationships={crossRecordRelationships}
              onContinue={() => {
                savePersonInOtherRecord(destRecord, warningPerson, currentImageArk)
              }}
            />
          )}
          <ErrorOverlay
            error={error}
            setError={setError}
            title={t('save-error-overlay.heading', 'Unable to Save Changes')}
            message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
          />
          <ErrorOverlay
            error={multipleRelationShipsWarning}
            setError={setMultipleRelationShipsWarning}
            title={t('edit-relationships.cross-record-warning-overlay.heading', 'Before Adding this Relationship')}
            message={t(
              'edit-relationships.cross-record-warning-overlay.multiple.content',
              'If you dont move all persons on the relationship the relationship would be lost'
            )}
            label={t('edit-relationships.cross-record-warning-overlay.remove.button', 'Remove Relationships')}
            callback={() => {
              setMultipleRelationShipsWarning(false)
              savePersonInOtherRecord(destRecord, warningPerson, currentImageArk)
            }}
            useDangerButton
          />
        </InfoSheetPanel>
      </DndContext>
    </ZionForm>
  )
}

export default DragNames
